import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Box } from 'grid-styled';
import { FormattedMessage } from 'react-intl';
import { withLastLocation } from 'react-router-last-location';
import { Mutation, Query } from 'react-apollo';

import {
  MUTATION_ADD_ANALYSE,
  MUTATION_EDIT_ANALYSE,
  QUERY_GET_ANALYSE,
  QUERY_GET_VIDEO_ANALYSES
} from '../../services/graphql/video-analyse-query';
import {
  ROUTE_ANALYSE_OVERVIEW,
  ROUTE_ANALYSE_INFO,
  ROUTE_ANALYSE_SELECT
} from 'RouteList';

import ListIconButton from 'components/ListIconButton/ListIconButton';
import H2 from 'components/H2/H2';
import H3 from 'components/H3/H3';
import P from 'components/P/P';
import Button from 'components/Button/Button';
import { ANALYSE_BUILDING_BLOCKS, ANALYSE_TIME_RANGE } from '../../constants';
import messages from './messages';

@inject('authStore', 'routing', 'uiState')
@observer
class Select extends Component {
  state = {
    maximumBlocks: 2,
    hasMinimumBlocks: false,
    hasMaximumBlocks: false,
    selected: []
  };

  selectedCats = [];

  componentDidMount() {
    window.scrollTo(0, 0);
    const {
      routing: { push },
      lastLocation,
      videoAnalyseId,
      analyseId
    } = this.props;
    if (
      lastLocation &&
      lastLocation.pathname.includes('/analyse/info') &&
      analyseId
    ) {
      push(`${ROUTE_ANALYSE_SELECT}/${videoAnalyseId}/${analyseId}`);
    }
  }

  async gotoInfoPage(mutation) {
    const {
      routing: { push },
      videoAnalyseId,
      analyseId,
      uiState
    } = this.props;
    const buildingBlocks = this.state.selected.sort((a, b) => a - b);

    let response;
    try {
      response = await mutation({
        variables: {
          id: videoAnalyseId,
          videoAnalyseId,
          buildingBlocks,
          finished: false,
          timeRange: ANALYSE_TIME_RANGE
        }
      });
    } catch (error) {
      response = { error };
    }

    if (!response.error) {
      push(
        `${ROUTE_ANALYSE_INFO}/${videoAnalyseId}/${
          analyseId ? analyseId : response.data.addAnalyse.id
        }`
      );
    } else {
      uiState.showError(response.error.message);
    }
  }

  toggleBlock = id => {
    let { selected } = this.state;
    let { showMaximumBlocks, hasMinimumBlocks } = this.state;

    if (selected.includes(id)) {
      const index = selected.indexOf(id);
      if (index !== -1) {
        selected.splice(index, 1);
      }
    } else if (selected.length < this.state.maximumBlocks) {
      selected.push(id);
      showMaximumBlocks = false;
    } else {
      showMaximumBlocks = true;
    }

    selected = selected.sort((a, b) => {
      return a - b;
    });

    hasMinimumBlocks = selected.length > 0;

    this.setState({
      selected,
      hasMinimumBlocks,
      showMaximumBlocks
    });
  };

  goBack = () => {
    const { routing: { push }, videoAnalyseId } = this.props;
    push(`${ROUTE_ANALYSE_OVERVIEW}/${videoAnalyseId}`);
  };

  render() {
    const { intl, authStore: { isImpersonator, user }, analyseId } = this.props;
    return (
      <Query
        query={QUERY_GET_ANALYSE}
        variables={{ analyseId }}
        fetchPolicy={'cache-and-network'}
        onCompleted={data => {
          const { buildingBlocks } = data.getAnalyse;
          this.setState({
            selected: buildingBlocks,
            hasMinimumBlocks: buildingBlocks.length > 0
          });
        }}
        skip={!analyseId}
      >
        {({ loading, error, data }) => {
          if (loading) return <P>Bezig met laden...</P>;
          if (error) return <P danger>{error.message}</P>;

          return (
            <Flex flexWrap="wrap" style={{ textAlign: 'left' }} px={4}>
              <Box width={1 / 1} mt={4} mb={2}>
                <H2 upper>
                  <FormattedMessage {...messages.analiseSelectTitle} />
                </H2>
              </Box>
              <Box width={1 / 1} mt={1} mb={4}>
                <FormattedMessage {...messages.analiseSelectIntro} />
              </Box>

              {ANALYSE_BUILDING_BLOCKS.map(block => (
                <BuildingBlockCategory
                  key={block.id}
                  title={block.title}
                  data={block.data}
                  toggleBlock={this.toggleBlock}
                  selected={this.state.selected}
                />
              ))}

              <Box width={1 / 1} mt={2}>
                <Button
                  primary
                  onClick={() => {
                    this.goBack();
                  }}
                  style={{ marginRight: 8 }}
                >
                  <FormattedMessage {...messages.analyseSelectBackButton} />
                </Button>
                {/*@TODO: afstemmen met backend*/}
                <Mutation
                  mutation={
                    analyseId ? MUTATION_EDIT_ANALYSE : MUTATION_ADD_ANALYSE
                  }
                  refetchQueries={[
                    {
                      query: QUERY_GET_VIDEO_ANALYSES,
                      variables: {
                        personId: isImpersonator ? isImpersonator.id : user.id
                      }
                    }
                  ]}
                >
                  {(createAnalyse, data) => {
                    return (
                      <Button
                        primary
                        disabled={!this.state.hasMinimumBlocks || data.loading}
                        onClick={() => {
                          this.gotoInfoPage(createAnalyse);
                        }}
                      >
                        <FormattedMessage {...messages.analiseSelectButton} />
                      </Button>
                    );
                  }}
                </Mutation>
              </Box>
            </Flex>
          );
        }}
      </Query>
    );
  }
}

const BuildingBlockCategory = ({ title = '', data, selected, toggleBlock }) => (
  <Flex width={1} flexWrap="wrap" mb={[2, 4]}>
    <Box width={1} mb={3}>
      <H3 upper textleft underlined lighter>
        {title}
      </H3>
    </Box>

    {data.map(block => (
      <BuildingBlockColumn
        key={block.id}
        title={block.title}
        items={block.items}
        toggleBlock={toggleBlock}
        selected={selected}
      />
    ))}
  </Flex>
);

const BuildingBlockColumn = ({
  title,
  selected = [],
  items = [],
  toggleBlock = id =>
    console.log('no toggleBlock function given to BuildingBlockColumn', id)
}) => (
  <Flex width={1 / 2} flexWrap="wrap" pr={4}>
    <Box width={1}>
      <Box mb={2}>
        <P upper bold>
          {title}
        </P>
      </Box>
      {items.map(data => (
        <Box mb={2} key={data.id} width={1}>
          <ListIconButton
            key={data.id}
            statusColor={selected.includes(data.id) ? 'success' : 'primary'}
            statusIcon={selected.includes(data.id) ? 'check' : ''}
            label={data.label}
            onClick={() => {
              toggleBlock(data.id);
            }}
          />
        </Box>
      ))}
    </Box>
  </Flex>
);

export default withLastLocation(Select);



// WEBPACK FOOTER //
// ./src/containers/analyse/Select.js